import React, { FC, useState } from 'react';
import Modal from 'components/Modal/Modal';
import Form from 'components/Form/Form';
import { FormItem } from 'components/Form/FormItem';
import { Input, TextArea } from 'components/Input';
import { Radio, RadioGroup } from 'components/Radio/Radio';
import { FileUploader } from 'components/FileUploader/FileUploader';
import { Text } from 'components/Typography';
import { ModalWidthEnum } from 'components/Modal/Modal.types';
import { validationRules } from 'components/Form/validationRules';
import useForm from 'hooks/useForm';
import { useCreateSupportTicketMutation } from 'redux/apiSlices/support.slice';
import { Colors } from 'core/CssVariables';
import { TUploadFile } from 'components/FileUploader/FileUploader.type';
import { toast } from 'components/Notification';
import {
  THelpSupportModalProps,
  TSupportTicketFormValues,
  SupportTicketType,
  TSupportTicketResponse,
} from './HelpSupportModal.types';
import { SHelpSupportForm, SSuccessMessage } from './HelpSupportModal.style';

const TITLE_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 2000;
const MAX_ATTACHMENTS = 5;
const ALLOWED_FILE_TYPES =
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const HelpSupportModal: FC<THelpSupportModalProps> = ({ visible, onClose }) => {
  const { form } = useForm();
  const [attachments, setAttachments] = useState<TUploadFile[]>([]);
  const [titleLength, setTitleLength] = useState(0);
  const [descriptionLength, setDescriptionLength] = useState(0);
  const [successResponse, setSuccessResponse] =
    useState<TSupportTicketResponse | null>(null);

  const [createSupportTicket, { isLoading, isSuccess, reset }] =
    useCreateSupportTicketMutation();

  const handleClose = () => {
    form.resetFields();
    setAttachments([]);
    setTitleLength(0);
    setDescriptionLength(0);
    setSuccessResponse(null);
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values: TSupportTicketFormValues = await form.validateFields();

      const formData = new FormData();
      formData.append('type', values.type);
      formData.append('title', values.title.trim());
      formData.append('description', values.description.trim());

      attachments.forEach(file => {
        if (file.originFileObj) {
          formData.append('attachments', file.originFileObj);
        }
      });

      const response = await createSupportTicket(formData).unwrap();
      setSuccessResponse(response);
    } catch (err: any) {
      // Show error notification for API errors
      if (err?.status) {
        toast({
          type: 'error',
          title: 'Failed to create ticket',
          description:
            err?.data?.message ||
            'Unable to create ticket. Please try again later.',
        });
      }
    }
  };

  const handleAttachmentsChange = (fileList: TUploadFile[]) => {
    const limitedFiles = fileList.slice(0, MAX_ATTACHMENTS);
    setAttachments(limitedFiles);
  };

  const renderSuccessContent = () => (
    <SSuccessMessage>
      <Text fontlevel={3} fontWeight={600} color={Colors.SuccessColor}>
        Ticket Created Successfully!
      </Text>
      <div className="ticket-link">
        <Text fontlevel={5}>
          Your support ticket <strong>{successResponse?.ticketKey}</strong> has
          been created.
        </Text>
      </div>
    </SSuccessMessage>
  );

  const renderFormContent = () => (
    <SHelpSupportForm>
      <Form form={form} layout="vertical">
        <FormItem
          name="type"
          label="Request Type"
          rules={[validationRules.required('request type')]}
          initialValue={SupportTicketType.BUG}
        >
          <RadioGroup className="radio-group">
            <Radio value={SupportTicketType.BUG} label="Bug Report" />
            <Radio
              value={SupportTicketType.FEATURE_REQUEST}
              label="Feature Request"
            />
            <Radio value={SupportTicketType.SUPPORT} label="Support Request" />
          </RadioGroup>
        </FormItem>

        <FormItem
          name="title"
          label="Title"
          rules={[
            validationRules.required('title'),
            {
              max: TITLE_MAX_LENGTH,
              message: `Title must not exceed ${TITLE_MAX_LENGTH} characters`,
            },
          ]}
        >
          <Input
            placeholder="Brief summary of your request"
            maxLength={TITLE_MAX_LENGTH}
            onChange={e => setTitleLength(e.target.value.length)}
          />
        </FormItem>
        <div
          className={`character-count ${
            titleLength > TITLE_MAX_LENGTH ? 'character-count-error' : ''
          }`}
        >
          {titleLength}/{TITLE_MAX_LENGTH}
        </div>

        <FormItem
          name="description"
          label="Description"
          rules={[
            validationRules.required('description'),
            {
              max: DESCRIPTION_MAX_LENGTH,
              message: `Description must not exceed ${DESCRIPTION_MAX_LENGTH} characters`,
            },
          ]}
        >
          <TextArea
            placeholder="Please provide detailed information about your request"
            rows={5}
            maxLength={DESCRIPTION_MAX_LENGTH}
            onChange={e => setDescriptionLength(e.target.value.length)}
          />
        </FormItem>
        <div
          className={`character-count ${
            descriptionLength > DESCRIPTION_MAX_LENGTH
              ? 'character-count-error'
              : ''
          }`}
        >
          {descriptionLength}/{DESCRIPTION_MAX_LENGTH}
        </div>

        <FormItem
          label={`Attachments (max ${MAX_ATTACHMENTS} files, 5MB each)`}
        >
          <FileUploader
            defaultFileList={attachments}
            onChange={handleAttachmentsChange}
            accept={ALLOWED_FILE_TYPES}
            skipUpload
          />
          <div className="file-types-hint">
            Allowed: Images, PDF, Text, CSV, Word, Excel
          </div>
        </FormItem>
      </Form>
    </SHelpSupportForm>
  );

  return (
    <Modal
      visible={visible}
      title={isSuccess ? 'Success' : 'Submit Support Request'}
      onCancel={handleClose}
      onOk={isSuccess ? handleClose : handleSubmit}
      okText={isSuccess ? 'Close' : 'Submit'}
      cancelButtonProps={{
        style: { display: isSuccess ? 'none' : 'inline-block' },
      }}
      confirmLoading={isLoading}
      size={ModalWidthEnum.Medium}
      destroyOnClose
    >
      {isSuccess ? renderSuccessContent() : renderFormContent()}
    </Modal>
  );
};

export default HelpSupportModal;
